/**
 * Thông báo nổi — dựng lại `toast()` của bản thiết kế gốc.
 *
 * Bản gốc dùng thông báo này ở 51 chỗ: bấm gì cũng có phản hồi ngay. Thiếu nó
 * thì người dùng bấm xong không biết đã ăn hay chưa, và đó là lý do phải dựng
 * lại thay vì để mỗi màn tự xoay xở.
 *
 * Dùng:
 *   const { bao, baoLoi } = dungThongBao();
 *   bao('Đã lưu thay đổi');
 *   baoLoi('Không lưu được: ...');
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type Loai = 'thuong' | 'tot' | 'loi';

interface Mau {
  id: number;
  loai: Loai;
  noiDung: string;
}

interface GiaTri {
  bao: (noiDung: string) => void;
  baoTot: (noiDung: string) => void;
  baoLoi: (noiDung: string) => void;
}

const BoiCanh = createContext<GiaTri | null>(null);

/** Bao lâu thì thông báo tự tắt. Lỗi để lâu hơn vì thường cần đọc kỹ. */
const GIAY_HIEN: Record<Loai, number> = { thuong: 2600, tot: 2600, loi: 6000 };

export function NhaCungCapThongBao({ children }: { children: ReactNode }) {
  const [dsMau, setDsMau] = useState<Mau[]>([]);
  const dem = useRef(0);

  const them = useCallback((loai: Loai, noiDung: string) => {
    const id = ++dem.current;
    setDsMau((cu) => [...cu, { id, loai, noiDung }]);
    setTimeout(() => setDsMau((cu) => cu.filter((m) => m.id !== id)), GIAY_HIEN[loai]);
  }, []);

  const giaTri = useMemo<GiaTri>(
    () => ({
      bao: (n) => them('thuong', n),
      baoTot: (n) => them('tot', n),
      baoLoi: (n) => them('loi', n),
    }),
    [them],
  );

  return (
    <BoiCanh.Provider value={giaTri}>
      {children}
      <div className="ag-thong-bao" role="status" aria-live="polite">
        {dsMau.map((m) => (
          <div key={m.id} className={`ag-thong-bao__mau ag-thong-bao__mau--${m.loai}`}>
            {m.noiDung}
          </div>
        ))}
      </div>
    </BoiCanh.Provider>
  );
}

export function dungThongBao(): GiaTri {
  const v = useContext(BoiCanh);
  if (!v) throw new Error('dungThongBao() phải nằm trong <NhaCungCapThongBao>');
  return v;
}

/**
 * Rút câu lỗi đọc được từ bất cứ thứ gì ném ra.
 *
 * Backend trả `{ ok: false, loi: "..." }` và `goiApi` đã dựng thành `Error` với
 * đúng câu đó, nên phần lớn trường hợp chỉ cần `.message`.
 */
export function cauLoi(loi: unknown): string {
  if (loi instanceof Error) return loi.message;
  if (typeof loi === 'string') return loi;
  return 'Có lỗi xảy ra, thử lại sau';
}
